import type { ListViewFilter } from 'App/resources/AllIWant';
import type { PropertyArray } from 'App/components/MultiActionModal/MultiActionModal';
import { filterObjToArray, removeEmptyFilters } from 'App/components/ListView/ListViewHelpers';
import DatePreset from 'App/services/datePresetService';

export const LIST_SEARCH_FILTER = 'ListSearch';

export type CampaignSelection = {
	selectionFilters: ListViewFilter[];
	selectionSearch?: string;
};

type DateValue = { preset?: string; start?: unknown; end?: unknown };

const UNPINNED_PRESETS = ['custom', 'whenever', 'hasValue', 'hasNoValue'];

const dateValueOf = (value: unknown): DateValue | null =>
	value && typeof value === 'object' && !Array.isArray(value) ? (value as DateValue) : null;

const isEmptyBound = (bound: unknown) => bound === undefined || bound === null || bound === '';

// What filterParse reads as no filter at all. A null value is not one of them: radio filters
// such as SubAccounts keep their meaning in comparisonType.
const narrowsNothing = (value: unknown) => {
	const range = dateValueOf(value);
	return (
		!!range &&
		Object.keys(range).every(key => ['preset', 'start', 'end'].includes(key)) &&
		(range.preset === 'whenever' ||
			((!range.preset || range.preset === 'custom') && isEmptyBound(range.start) && isEmptyBound(range.end)))
	);
};

const withSelf = (value: unknown, selfId: number): unknown => {
	if (Array.isArray(value)) {
		return value.map(entry => (entry === 'self' ? selfId : withSelf(entry, selfId)));
	}
	return value && typeof value === 'object'
		? Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, withSelf(entry, selfId)]))
		: value;
};

// A relative date or 'self' is read again whenever the list opens, against that day and viewer;
// pinned to the run's dates and user, Open in list shows what this run added.
const pinned = (filter: ListViewFilter, selfId?: number): ListViewFilter => {
	const value = selfId ? withSelf(filter.value, selfId) : filter.value;
	const range = dateValueOf(value);
	if (!range?.preset || UNPINNED_PRESETS.includes(range.preset)) {
		return { ...filter, value };
	}
	const { start = null, end = null } = DatePreset.getDatesFromValue(range) ?? {};
	return { ...filter, value: start || end ? { ...range, preset: 'custom', start, end } : value };
};

// getFiltersFromView renames a view's own custom fields to `<view type>.Custom_N`; the saved view
// and ui-3 key them `Custom_N`, and ui-3 drops a name it does not know.
const asViewName = (filter: ListViewFilter, viewType?: string): ListViewFilter => {
	const prefix = `${viewType}.`;
	return viewType && filter.filterName?.startsWith(`${prefix}Custom_`)
		? { ...filter, filterName: filter.filterName.slice(prefix.length) }
		: filter;
};

/** The list's quick search travels as its own term, the way ui-3 keeps it apart from the filters. */
export const campaignSelectionFromFilters = (
	filters: { [filterName: string]: ListViewFilter },
	viewType?: string,
	selfId?: number
): CampaignSelection => {
	const { [LIST_SEARCH_FILTER]: search, ...rest } = filters;
	const term = typeof search?.value === 'string' ? search.value : '';
	return {
		selectionFilters: filterObjToArray(removeEmptyFilters(rest))
			.filter(filter => !filter.inactive && !narrowsNothing(filter.value))
			.map(filter => pinned(asViewName(filter, viewType), selfId)),
		...(term ? { selectionSearch: term } : {})
	};
};

export const campaignSelectionParams = (
	properties: PropertyArray,
	selection: CampaignSelection | undefined
): CampaignSelection | Record<string, never> =>
	selection && properties.some(property => property.name === 'AddProject') ? selection : {};
